import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, FolderKanban, Trash2, ChevronRight } from "lucide-react";
import GoogleAdsPanel from "@/components/GoogleAdsPanel";

export default function Reports() {
  const { canEdit, slug } = useWs();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const isAdmin = canEdit;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/channels");
      setChannels(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/channels", { name });
      toast.success("Channel added");
      setOpen(false);
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmDelete({ message: "Delete this channel and its reports?" }))) return;
    await api.delete(`/channels/${id}`);
    toast.success("Channel deleted");
    load();
  };

  return (
    <div>
      <PageHeader
        title="Reports"
        subtitle="Your report library, organised by marketing channel."
        action={
          isAdmin && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-channel-button" className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
                  <Plus className="h-4 w-4 mr-1.5" /> Add channel
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                <DialogHeader><DialogTitle className="font-display">New channel</DialogTitle></DialogHeader>
                <div className="py-2">
                  <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Channel name</label>
                  <Input data-testid="channel-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. TikTok" className="bg-[#F6F6FD] border-[#EEEFEF]" />
                </div>
                <DialogFooter>
                  <Button data-testid="channel-submit-button" onClick={create} disabled={!name} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Add channel</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <GoogleAdsPanel />

      {loading ? (
        <div className="text-[#9FA1A4] text-sm">Loading…</div>
      ) : channels.length === 0 ? (
        <EmptyState icon={FolderKanban} title="No channels" description="Add a marketing channel to organise reports." testid="reports-empty" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {channels.map((c) => (
            <div
              key={c.id}
              data-testid="report-channel-card"
              onClick={() => navigate(`/${slug}/reports/${c.id}`)}
              className="group cursor-pointer rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-6 hover:-translate-y-1 hover:border-[#d3d4da] transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg bg-[#F6F6FD] border border-[#f0f0f3] flex items-center justify-center">
                  <FolderKanban className="h-5 w-5 text-[#9FA1A4]" />
                </div>
                {isAdmin && (
                  <button data-testid="channel-delete-button" onClick={(e) => remove(c.id, e)} className="opacity-0 group-hover:opacity-100 text-[#c4c5cc] hover:text-rose-600 transition-all">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <h3 className="mt-4 font-display text-lg font-medium text-[#06060C]">{c.name}</h3>
              <div className="mt-1 flex items-center justify-between">
                <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{c.report_count} report{c.report_count === 1 ? "" : "s"}</p>
                <ChevronRight className="h-4 w-4 text-[#c4c5cc] group-hover:text-[#06060C]/50 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
